/**
 * What a joystick *is*, as far as this port is concerned: four axes, a list of
 * named actions on buttons, a hat, and the four numbers calibration measures.
 *
 * ## Why the mapping is per device and persisted by `Gamepad.id`
 *
 * Sticks disagree about everything. Axis order is not standardised outside the
 * W3C "standard gamepad" layout; a hat is four buttons on a pad and a single
 * nine-valued axis on most DirectInput sticks; a throttle rests at an end on a
 * quadrant, at the centre on a twist grip, and either way round depending on
 * whose driver wrote the descriptor. None of that can be guessed from the
 * numbers alone, so the port measures it once (`gamepad-calibration.ts`) and
 * keeps the answer under the device's own `id`. Plug the pad back in next week
 * and it is still calibrated; plug a different stick in and it gets its own
 * record rather than inheriting the pad's.
 *
 * ## …and why the standard layout still needs no setup
 *
 * When the browser reports `mapping === "standard"` it is promising the W3C
 * button/axis order, which is enough to bind everything without asking. That is
 * `STANDARD_AXES` / `STANDARD_BUTTONS` below, and it is why an ordinary
 * controller flies the aeroplane the moment it is plugged in. The one thing
 * that layout does *not* give is a throttle: axis 3 is a spring-centred stick,
 * and a lever that snaps back to the middle is not a throttle. So the standard
 * default leaves `throttle` unmapped and puts throttle up/down on the shoulder
 * buttons, and a real quadrant gets its axis from calibration.
 *
 * Nothing here touches the DOM or the clock. `gamepad.ts` is the only file that
 * knows `navigator.getGamepads` exists.
 */

import type { SettingsStore } from "../audio/settings.ts";
import type { DiscreteCommand } from "./keys.ts";

/** The four sim controls an axis can drive; `throttle` is the one that is 0…1 rather than −1…+1. */
export const AXIS_ROLES = ["pitch", "roll", "yaw", "throttle"] as const;
export type AxisRole = (typeof AXIS_ROLES)[number];

/** Verbatim on the CONTROLS panel, in the port's own voice. */
export const AXIS_ROLE_LABELS: Readonly<Record<AxisRole, string>> = {
  pitch: "ELEVATOR",
  roll: "AILERON",
  yaw: "RUDDER",
  throttle: "THROTTLE",
};

export interface AxisMapping {
  /** Index into `Gamepad.axes`; `UNMAPPED` when this role has no axis on this device. */
  readonly index: number;
  /** True when the device reports the opposite sign from the sim's own sense. */
  readonly invert: boolean;
  /** The raw reading at rest, as calibration measured it. */
  readonly centre: number;
  /** Raw travel from `centre` to a limit. Never zero — `axisReading` divides by it. */
  readonly travel: number;
}

/** The index that means "this role has no axis on this device". */
export const UNMAPPED = -1;

export const UNMAPPED_AXIS: AxisMapping = { index: UNMAPPED, invert: false, centre: 0, travel: 1 };

/**
 * Everything a stick button can be asked to do, and the whole of it: every
 * entry is a binding the keyboard already has (`keys.ts`), so a player who
 * knows the key map knows what the stick does. Held actions and one-shots are
 * mixed in one list because a *binding* does not care which it is — the
 * distinction is `HELD_ACTIONS` below, and it is what decides whether the
 * button's level or its rising edge is read.
 */
export const PAD_ACTIONS = [
  "fire",
  "bombRelease",
  "clearJam",
  "throttleUp",
  "throttleDown",
  "lookBack",
  "cameraCycle",
  "map",
  "pause",
  "timeCompress",
  "preferences",
] as const;
export type PadAction = (typeof PAD_ACTIONS)[number];

/**
 * The actions read as a *level* — held down means held down, exactly as the
 * matching key does. Everything else is a one-shot resolved on the rising edge,
 * for the same reason `keyboard.ts` guards `discreteCommandFor` with
 * `!event.repeat`: holding a button must not toggle pause sixty times a second.
 */
export const HELD_ACTIONS: ReadonlySet<PadAction> = new Set<PadAction>([
  "fire",
  "bombRelease",
  "clearJam",
  "throttleUp",
  "throttleDown",
  "lookBack",
]);

/** What the CONTROLS panel prints beside each binding's well. */
export const PAD_ACTION_LABELS: Readonly<Record<PadAction, string>> = {
  fire: "FIRE GUNS",
  bombRelease: "DROP BOMB",
  clearJam: "CLEAR JAM",
  throttleUp: "THROTTLE UP",
  throttleDown: "THROTTLE DN",
  lookBack: "LOOK BACK",
  cameraCycle: "CAMERA",
  map: "MAP",
  pause: "PAUSE",
  timeCompress: "TIME COMP",
  preferences: "PREFERENCES",
};

/**
 * The one-shot actions that are already a `DiscreteCommand`, so a stick button
 * runs the same code path the key does rather than a parallel one — the camera
 * commands, the two full-screen surfaces, pause and time compression all arrive
 * at `Fly.tsx`'s `runCommand` by the same door.
 *
 * `muteAudio` is deliberately absent: `V` is one key, a stick has few buttons,
 * and there is nothing to mute that the PREFERENCES panel cannot reach.
 */
export const PAD_COMMANDS: Readonly<Partial<Record<PadAction, DiscreteCommand>>> = {
  cameraCycle: "cameraCycle",
  map: "map",
  pause: "pause",
  timeCompress: "timeCompress",
  preferences: "preferences",
};

/**
 * Where the hat lives, which is the one piece of a stick that has two genuinely
 * different shapes rather than two orderings of the same shape.
 *
 * - `buttons` — the W3C standard layout's D-pad, four ordinary buttons.
 * - `axis` — the DirectInput convention every non-standard stick uses: one axis
 *   carrying eight detents and a centre, decoded by `hatDirectionOf`.
 */
export type HatSource =
  | { readonly kind: "none" }
  | { readonly kind: "buttons"; readonly up: number; readonly down: number; readonly left: number; readonly right: number }
  | { readonly kind: "axis"; readonly index: number };

export const NO_HAT: HatSource = { kind: "none" };

/** How hard the response curve bends. `linear` is the identity; the other two soften the centre. */
export const RESPONSE_CURVES = ["linear", "smooth", "fine"] as const;
export type ResponseCurve = (typeof RESPONSE_CURVES)[number];

export const RESPONSE_CURVE_LABELS: Readonly<Record<ResponseCurve, string>> = {
  linear: "LINEAR",
  smooth: "SMOOTH",
  fine: "FINE",
};

export interface GamepadMapping {
  /** The `Gamepad.id` this was measured against — the storage key, kept in the value so a record is self-describing. */
  readonly deviceId: string;
  readonly axes: Readonly<Record<AxisRole, AxisMapping>>;
  /** `UNMAPPED` for an action this device has no button for. */
  readonly buttons: Readonly<Record<PadAction, number>>;
  readonly hat: HatSource;
  /** Fraction of travel around centre that reads as zero, 0…`MAX_DEAD_ZONE`. */
  readonly deadZone: number;
  readonly curve: ResponseCurve;
}

/**
 * The W3C standard gamepad's axis order. `pitch` is **not** inverted: the
 * browser reports a stick pushed away from you as −1 on the Y axis, and −1 is
 * this sim's forward stick (nose down) too, so the two senses already agree.
 */
const STANDARD_AXES: Readonly<Record<AxisRole, AxisMapping>> = {
  roll: { index: 0, invert: false, centre: 0, travel: 1 },
  pitch: { index: 1, invert: false, centre: 0, travel: 1 },
  yaw: { index: 2, invert: false, centre: 0, travel: 1 },
  // Axis 3 is the right stick's Y, which springs back to centre. A throttle
  // that returns to the middle when you let go is not a throttle, so the
  // standard layout gets its throttle from the shoulder buttons instead.
  throttle: UNMAPPED_AXIS,
};

/**
 * The W3C standard gamepad's buttons, bound to the same jobs the keyboard has.
 * The trigger is the trigger (button 7 is the right analogue trigger) and the
 * left trigger checks six, which is where a hand reaches for both.
 */
const STANDARD_BUTTONS: Readonly<Record<PadAction, number>> = {
  fire: 7,
  lookBack: 6,
  throttleUp: 5,
  throttleDown: 4,
  bombRelease: 0,
  map: 1,
  clearJam: 2,
  cameraCycle: 3,
  timeCompress: 8,
  pause: 9,
  preferences: 11,
};

/** The standard layout's D-pad. */
const STANDARD_HAT: HatSource = { kind: "buttons", up: 12, down: 13, left: 14, right: 15 };

/**
 * A tenth of travel. Generous for a good stick and barely enough for a worn
 * thumbstick, which is why it is a setting — but it has to default to the value
 * that stops a *drifting* device flying the aeroplane by itself, because a
 * player whose aircraft rolls with nobody touching it does not reach for the
 * dead-zone control, he concludes the joystick support is broken.
 */
export const DEFAULT_DEAD_ZONE = 0.1;
export const MAX_DEAD_ZONE = 0.3;
export const DEAD_ZONE_STEP = 0.05;

/**
 * What a device gets before anybody calibrates it. A standard-mapping pad gets
 * the whole W3C layout and needs no setup at all; anything else gets the two
 * axes every stick agrees about (X is roll, Y is pitch), the trigger on button
 * 0, and the DirectInput hat axis if the device is long enough to have one —
 * enough to fly with while the player finds CALIBRATE, and not a guess about
 * anything a guess would get wrong.
 */
export function defaultMapping(deviceId: string, standard: boolean, axisCount = 0): GamepadMapping {
  if (standard) {
    return { deviceId, axes: STANDARD_AXES, buttons: STANDARD_BUTTONS, hat: STANDARD_HAT, deadZone: DEFAULT_DEAD_ZONE, curve: "linear" };
  }
  const buttons = Object.fromEntries(PAD_ACTIONS.map((action) => [action, UNMAPPED])) as Record<PadAction, number>;
  return {
    deviceId,
    axes: {
      roll: { index: 0, invert: false, centre: 0, travel: 1 },
      pitch: { index: 1, invert: false, centre: 0, travel: 1 },
      yaw: UNMAPPED_AXIS,
      throttle: UNMAPPED_AXIS,
    },
    buttons: { ...buttons, fire: 0 },
    hat: axisCount > HAT_AXIS_INDEX ? { kind: "axis", index: HAT_AXIS_INDEX } : NO_HAT,
    deadZone: DEFAULT_DEAD_ZONE,
    curve: "linear",
  };
}

/** Where the DirectInput hat lands on a stick that reports it as an axis. */
export const HAT_AXIS_INDEX = 9;

export const GAMEPAD_STORAGE_KEY = "red-opus/gamepad-bindings";

/**
 * How many devices' records are kept. A browser hands out a fresh `Gamepad.id`
 * for every make and model that has ever been plugged in, so without a cap this
 * key grows forever; eight is more sticks than anyone has and small enough that
 * the whole record stays a single short string.
 */
export const MAX_STORED_DEVICES = 8;

export interface GamepadBindings {
  /** Keyed by `Gamepad.id`, oldest first — which is the order `rememberMapping` drops from. */
  readonly devices: readonly GamepadMapping[];
}

export const NO_BINDINGS: GamepadBindings = { devices: [] };

function clampNumber(value: unknown, min: number, max: number, fallback: number): number {
  const num = typeof value === "number" ? value : Number.NaN;
  if (!Number.isFinite(num)) return fallback;
  return Math.min(max, Math.max(min, num));
}

/**
 * The band a calibrated `travel` can land in. The floor is `CALIBRATION_MIN_TRAVEL`'s
 * own threshold — the flow refuses to bind an axis that moved less than that,
 * so a smaller number in storage did not come from this port. The ceiling is a
 * hat-sized swing, which is the largest movement any axis can report.
 */
const MIN_TRAVEL = 0.35;
const MAX_TRAVEL = 4.5;

function normalizeAxis(candidate: unknown): AxisMapping {
  if (typeof candidate !== "object" || candidate === null) return UNMAPPED_AXIS;
  const raw = candidate as Partial<Record<keyof AxisMapping, unknown>>;
  const index = Number.isInteger(raw.index) ? (raw.index as number) : UNMAPPED;
  // `travel` is the divisor in `calibrated()`, so a stored zero is an axis that
  // reads Infinity. **Falls back rather than clamping**: clamping a zero to the
  // smallest allowed travel would give a hair-trigger axis at full deflection
  // on the least movement, which is a worse failure than Infinity because it
  // looks like flying. Anything outside the range calibration can produce reads
  // as the uncalibrated 1.
  const travel = Number.isFinite(raw.travel) && (raw.travel as number) >= MIN_TRAVEL && (raw.travel as number) <= MAX_TRAVEL ? (raw.travel as number) : 1;
  return { index: index < 0 ? UNMAPPED : index, invert: raw.invert === true, centre: clampNumber(raw.centre, -1, 1, 0), travel };
}

function normalizeHat(candidate: unknown): HatSource {
  if (typeof candidate !== "object" || candidate === null) return NO_HAT;
  const raw = candidate as Record<string, unknown>;
  if (raw["kind"] === "axis" && Number.isInteger(raw["index"])) return { kind: "axis", index: raw["index"] as number };
  if (raw["kind"] !== "buttons") return NO_HAT;
  const at = (name: string): number => (Number.isInteger(raw[name]) ? (raw[name] as number) : UNMAPPED);
  return { kind: "buttons", up: at("up"), down: at("down"), left: at("left"), right: at("right") };
}

/** Reads whatever survives a `JSON.parse` of untrusted stored text; never throws, never returns a half-record. */
export function normalizeMapping(candidate: unknown): GamepadMapping | undefined {
  if (typeof candidate !== "object" || candidate === null) return undefined;
  const raw = candidate as Record<string, unknown>;
  const deviceId = raw["deviceId"];
  if (typeof deviceId !== "string" || deviceId.length === 0) return undefined;
  const storedAxes = (typeof raw["axes"] === "object" && raw["axes"] !== null ? raw["axes"] : {}) as Record<string, unknown>;
  const storedButtons = (typeof raw["buttons"] === "object" && raw["buttons"] !== null ? raw["buttons"] : {}) as Record<string, unknown>;
  return {
    deviceId,
    axes: Object.fromEntries(AXIS_ROLES.map((role) => [role, normalizeAxis(storedAxes[role])])) as Record<AxisRole, AxisMapping>,
    buttons: Object.fromEntries(
      PAD_ACTIONS.map((action) => [action, Number.isInteger(storedButtons[action]) ? (storedButtons[action] as number) : UNMAPPED]),
    ) as Record<PadAction, number>,
    hat: normalizeHat(raw["hat"]),
    deadZone: clampNumber(raw["deadZone"], 0, MAX_DEAD_ZONE, DEFAULT_DEAD_ZONE),
    curve: RESPONSE_CURVES.find((name) => name === raw["curve"]) ?? "linear",
  };
}

export function normalizeBindings(candidate: unknown): GamepadBindings {
  if (typeof candidate !== "object" || candidate === null) return NO_BINDINGS;
  const raw = (candidate as Record<string, unknown>)["devices"];
  if (!Array.isArray(raw)) return NO_BINDINGS;
  const devices: GamepadMapping[] = [];
  for (const entry of raw) {
    const mapping = normalizeMapping(entry);
    // A duplicate id would make `mappingFor` depend on array order; the later
    // record is the one a `rememberMapping` would have written, so it wins.
    if (mapping) devices.push(mapping);
  }
  return { devices: devices.slice(-MAX_STORED_DEVICES) };
}

export function serializeBindings(bindings: GamepadBindings): string {
  return JSON.stringify(bindings);
}

/** Defaults on missing key, corrupt JSON, or a non-object payload — never throws. */
export function loadBindings(store: SettingsStore | undefined): GamepadBindings {
  const raw = store?.getItem(GAMEPAD_STORAGE_KEY) ?? null;
  if (raw === null) return NO_BINDINGS;
  try {
    return normalizeBindings(JSON.parse(raw));
  } catch {
    return NO_BINDINGS;
  }
}

export function saveBindings(store: SettingsStore | undefined, bindings: GamepadBindings): void {
  store?.setItem(GAMEPAD_STORAGE_KEY, serializeBindings(bindings));
}

/** The stored record for `deviceId`, or `undefined` when this device has never been set up. */
export function mappingFor(bindings: GamepadBindings, deviceId: string): GamepadMapping | undefined {
  for (let index = bindings.devices.length - 1; index >= 0; index--) {
    const candidate = bindings.devices[index];
    if (candidate && candidate.deviceId === deviceId) return candidate;
  }
  return undefined;
}

/** Replaces this device's record (or appends it), dropping the oldest once the cap is reached. */
export function rememberMapping(bindings: GamepadBindings, mapping: GamepadMapping): GamepadBindings {
  const kept = bindings.devices.filter((entry) => entry.deviceId !== mapping.deviceId);
  return { devices: [...kept, mapping].slice(-MAX_STORED_DEVICES) };
}
